import * as React from 'react';
import { QrReader } from '@blackbox-vision/react-qr-reader';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, ButtonGroup } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close'
import { GenericErrorModal } from '../GenericErrorModal';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

interface IQRCodeScanModalV2Props {
    hidden: boolean,
    changePage: (page: string) => void
    closeModal: () => void
}

export class QRCodeScanModalV2 extends React.Component<IQRCodeScanModalV2Props> {

    state = {
        qrCodeError: "",
        zoomSupported: null as null | boolean,   // true = optical zoom works
        appliedZoom: 0,                          // last applied optical zoom
        cssScale: 1,                             // last applied visual zoom (CSS)
        torchSupported: false,
        torchOn: false,
        scanningLocked: false,
    }

    private videoHostRef = React.createRef<HTMLDivElement>();

    // UA helpers (fallback hints when capabilities are missing)
    private isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
    private isAndroid = () => /Android/i.test(navigator.userAgent);
    private isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    private isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Helper: get the <video> element rendered by QrReader
    private getVideoEl = (): HTMLVideoElement | null => {
        const host = this.videoHostRef.current;
        if (!host) return null;
        return host.querySelector('video');
    };

    // Helper: get the MediaStreamTrack from the video
    private getVideoTrack = (): MediaStreamTrack | null => {
        const video = this.getVideoEl();
        const stream = (video?.srcObject as MediaStream) || null;
        if (!stream) return null;
        const tracks = stream.getVideoTracks();
        return tracks && tracks.length ? tracks[0] : null;
    };

    // Wait until the QrReader attaches the stream to the video
    private waitForVideoTrack = (timeoutMs = 5000): Promise<MediaStreamTrack | null> => {
        return new Promise(resolve => {
            const start = Date.now();
            const tryGet = () => {
                const track = this.getVideoTrack();
                if (track) return resolve(track);
                if (Date.now() - start > timeoutMs) return resolve(null);
                setTimeout(tryGet, 100);
            };
            tryGet();
        });
    };

    // Optical zoom (if supported by camera)
    private tryApplyZoom = async (zoom = 2) => {
        try {
            const track = await this.waitForVideoTrack();
            if (!track || !track.getCapabilities) {
                this.setState({ zoomSupported: false, appliedZoom: 0 });
                return false;
            }
            const caps: any = track.getCapabilities();
            if (!caps || !('zoom' in caps)) {
                this.setState({ zoomSupported: false, appliedZoom: 0 });
                return false;
            }
            const min = caps.zoom?.min ?? 1;
            const max = caps.zoom?.max ?? zoom;
            const clamped = Math.min(Math.max(zoom, min), max);
            await track.applyConstraints({ advanced: [{ zoom: clamped }] } as any);
            this.setState({ zoomSupported: true, appliedZoom: clamped });
            return true;
        } catch {
            this.setState({ zoomSupported: false, appliedZoom: 0 });
            return false;
        }
    };

    // Visual zoom (CSS scale) fallback for iOS Safari and devices without optical zoom
    private applyCssScale = (scale: number) => {
        const video = this.getVideoEl();
        if (!video) return;
        // Ensure the wrapper crops the scaled video
        const host = this.videoHostRef.current;
        if (host) {
            host.style.overflow = 'hidden';
        }
        // Scale around center
        video.style.transformOrigin = 'center center';
        video.style.transform = `scale(${scale})`;
        // Make sure the video fills the container so cropping looks natural
        video.style.objectFit = 'cover';
        this.setState({ cssScale: scale });
    };

    // Decide best option per platform/capability and set zoom level
    private setZoomLevel = async (level: number) => {
        // Try true optical zoom first if we already know it's supported
        if (this.state.zoomSupported === true) {
            const ok = await this.tryApplyZoom(level);
            if (ok) return;
        }
        // If unknown, probe once
        if (this.state.zoomSupported === null) {
            const ok = await this.tryApplyZoom(level);
            if (ok) return;
        }
        // Fallback: on iOS Safari and other unsupported devices, use CSS scale
        this.applyCssScale(level);
    };

    // Manually set zoom level from buttons
    private handleSetZoom = async (level: number) => {
        await this.setZoomLevel(level);
    };

    // Set continuous focus if the camera supports it (helps sharpness at close range)
    private setContinuousFocusIfAvailable = async () => {
        const track = await this.waitForVideoTrack();
        const caps: any = track?.getCapabilities?.();
        if (!track || !caps) return;
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
            try {
                await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] } as any);
            } catch { /* ignore */ }
        }
    };

    // Torch control (Android/Chrome on many devices)
    private setTorch = async (on: boolean) => {
        const track = await this.waitForVideoTrack();
        const caps: any = track?.getCapabilities?.();
        if (!track || !caps || !caps.torch) return;
        try {
            await track.applyConstraints({ advanced: [{ torch: on }] } as any);
            this.setState({ torchOn: on });
        } catch { /* ignore */ }
    };

    private probeTorchSupport = async () => {
        const track = await this.waitForVideoTrack();
        const caps: any = track?.getCapabilities?.();
        this.setState({ torchSupported: !!caps?.torch });
    };

    // Initialize on open: probe optical zoom at 1x without visual change
    componentDidUpdate(prevProps: IQRCodeScanModalV2Props) {
        if (prevProps.hidden !== this.props.hidden && this.props.hidden === false) {
            // Probe capabilities; if not supported and on iOS Safari, prep CSS fallback
            this.tryApplyZoom(1).then((ok) => {
                if (!ok && this.isIOS() && this.isSafari()) {
                    // Reset any previous CSS transform
                    this.applyCssScale(1);
                }
            });
            this.setContinuousFocusIfAvailable();
            this.probeTorchSupport();
            this.setState({ scanningLocked: false }); // reset
        }
        // When closing, reset CSS zoom
        if (prevProps.hidden === false && this.props.hidden === true) {
            const video = this.getVideoEl();
            if (video) {
                video.style.transform = '';
                video.style.transformOrigin = '';
                video.style.objectFit = '';
            }
            const host = this.videoHostRef.current;
            if (host) host.style.overflow = '';
            if (this.state.cssScale !== 1) this.setState({ cssScale: 1 });
            if (this.state.torchOn) this.setTorch(false);
        }
    }

    CloseErrorModal = () =>{
        this.setState({...this.state, qrCodeError: ""});
    }

    HandleQrReaderOnResult = (result: any, error: any) => {
        if (this.state.scanningLocked) return;
        if (result) {
            const text = result.getText?.() ?? result?.text ?? '';
            if (text) {
                this.setState({ scanningLocked: true }, () => this.props.changePage(text));
            } else {
                this.setState({ ...this.state, qrCodeError: "Incorrect QR code" });
            }
        }
        if (error) {
            this.setState({ ...this.state, qrCodeError: "" });
        }
    }

    render() {
        return (
            <div style={{ width: "100%" }}>
                <Dialog
                    onClose={this.props.closeModal}
                    aria-labelledby="simple-modal-title"
                    aria-describedby="simple-modal-description"
                    className="qrScannerModal"
                    open={this.props.hidden == false}
                >
                    <DialogTitle disableTypography className='dialog-title-warning'>
                        <Typography variant="h6">Scan QR Code</Typography>
                        <IconButton aria-label="close"
                            className='close-button'
                            onClick={this.props.closeModal}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <div ref={this.videoHostRef}>
                            <div style={{ height: "70%", minWidth: "250px" }}>
                                <QrReader
                                    onResult={(result, error) => this.HandleQrReaderOnResult(result, error)}
                                    constraints={{
                                        facingMode: { ideal: 'environment' },
                                    }}
                                />
                            </div>

                            {/* Status */}
                            {this.state.zoomSupported === true && this.state.appliedZoom > 0 &&
                                <Alert severity="success">Optical zoom set to {this.state.appliedZoom}x</Alert>}
                            {this.state.zoomSupported === false &&
                                <Alert severity="info">Optical zoom not supported; using visual zoom</Alert>}

                            {this.state.qrCodeError !== "" &&
                                <Alert severity="error">{this.state.qrCodeError}</Alert>}
                            {this.state.qrCodeError === "" &&
                                <Alert severity="info">QR Code Not Found</Alert>}
                        </div>
                        {/* Centered zoom controls */}
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 12 }}>
                            <ButtonGroup variant="outlined" color="primary">
                            <Button
                                onClick={() => this.handleSetZoom(1)}
                                variant={
                                    this.state.zoomSupported === true
                                    ? Math.abs((this.state.appliedZoom || 0) - 1) < 0.2 ? 'contained' : 'outlined'
                                    : Math.abs((this.state.cssScale || 0) - 1) < 0.05 ? 'contained' : 'outlined'
                                }
                                >
                                1x
                                </Button>
                                <Button
                                onClick={() => this.handleSetZoom(1.5)}
                                variant={
                                    this.state.zoomSupported === true
                                    ? Math.abs((this.state.appliedZoom || 0) - 1.5) < 0.2 ? 'contained' : 'outlined'
                                    : Math.abs((this.state.cssScale || 0) - 1.5) < 0.05 ? 'contained' : 'outlined'
                                }
                                >
                                1.5x
                                </Button>
                                <Button
                                onClick={() => this.handleSetZoom(2)}
                                variant={
                                    this.state.zoomSupported === true
                                    ? Math.abs((this.state.appliedZoom || 0) - 2) < 0.2 ? 'contained' : 'outlined'
                                    : Math.abs((this.state.cssScale || 0) - 2) < 0.05 ? 'contained' : 'outlined'
                                }
                                >
                                2x
                            </Button>
                            </ButtonGroup>

                            {/* Torch toggle when supported */}
                            {this.state.torchSupported && (
                                <ToggleButtonGroup exclusive size="small" value={this.state.torchOn ? 'on' : 'off'}>
                                    <ToggleButton value="off" onClick={() => this.setTorch(false)}>Light Off</ToggleButton>
                                    <ToggleButton value="on" onClick={() => this.setTorch(true)}>Light On</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions disableSpacing={true}>
                        <Button onClick={this.props.closeModal} className='action-button'>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}